// useInviteDialog — 「招待」ダイアログの開閉 state と、あいことば有無の lookup。
//
// あいことば付き部屋かどうかは、RoomSettingsModal と同じく dispatcher
// (`lookupRoomServer` の roomMeta.entryPasswordRequired) から取る。ダイアログを開いた
// ときに 1 回だけ問い合わせ、失敗時は null (= 不明、注意書きは出さない)。

import type { InviteAction } from '@oekaki/protocol';
import { useCallback, useEffect, useState } from 'react';

import { reportInvite } from '../../application/inviteAnalytics.js';
import { useSession } from './useSession.js';

export interface InviteDialogController {
  readonly open: boolean;
  readonly openDialog: () => void;
  readonly closeDialog: () => void;
  /** ダイアログ内の利用計装 (`analytics.invite`)。`InviteDialog.onAction` に渡す。 */
  readonly report: (action: InviteAction) => void;
  /** null = 未解決 / 不明。true のときだけ「あいことばは別に伝えてね」を出す。 */
  readonly entryPasswordRequired: boolean | null;
}

export const useInviteDialog = (roomId: string): InviteDialogController => {
  const { dispatcher, transport } = useSession();
  const [open, setOpen] = useState(false);
  const [entryPasswordRequired, setEntryPasswordRequired] = useState<boolean | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const out = await dispatcher.lookupRoomServer(roomId);
        if (cancelled) return;
        setEntryPasswordRequired(out.outcome === 'ok' ? out.roomMeta.entryPasswordRequired : null);
      } catch {
        if (!cancelled) setEntryPasswordRequired(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, dispatcher, roomId]);

  const report = useCallback(
    (action: InviteAction) => reportInvite(transport, action),
    [transport],
  );
  const openDialog = useCallback(() => {
    setOpen(true);
    report('open');
  }, [report]);
  const closeDialog = useCallback(() => setOpen(false), []);
  return { open, openDialog, closeDialog, report, entryPasswordRequired };
};
